import { Injectable } from '@angular/core';
import { Action, State, StateContext, dispatch } from '@ngxs/store';
import {
	UpsertClientIntakeFormContext,
	AddClientsToIntakeFormContext,
	RemoveClientsFromIntakeFormContext,
} from '@db-access/clientIntakeFormContext';
import {
	UpsertIntakeFormContext,
	AddRemoveClientContext,
} from './intake-form-context.actions';
import { firstValueFrom } from 'rxjs';
import type {
	ClientIntakeFormContextItem,
	ClientIntakeFormContext,
} from '@models/client-intake';
import { IntakeFormContextFsDocType } from '@models/client-intake';
import { syncContextClientsFromMatterClients } from '../utils/intake-form-context.utils';

@State({
	name: 'intakeFormContext',
	defaults: {},
})
@Injectable()
export class IntakeFormContextState {
	upsertClientIntakeFormContext = dispatch(UpsertClientIntakeFormContext);
	addClientsToContext = dispatch(AddClientsToIntakeFormContext);
	removeClientsFromContext = dispatch(RemoveClientsFromIntakeFormContext);

	@Action(UpsertIntakeFormContext)
	async upsertIntakeFormContext(
		ctx: StateContext<Record<string, never>>,
		action: UpsertIntakeFormContext,
	): Promise<void> {
		// Convert from public API format to internal discriminated union format
		const items = this.convertToIntakeFormContextItems(action.context);
		await firstValueFrom(this.upsertClientIntakeFormContext(items));
	}

	@Action(AddRemoveClientContext)
	async addRemoveClientContext(
		ctx: StateContext<Record<string, never>>,
		action: AddRemoveClientContext,
	): Promise<void> {
		const { clientsToAdd, clientsToRemove } =
			syncContextClientsFromMatterClients(
				action.payload.currentContext,
				action.payload.clients,
			);

		// Remove clients first, then add new ones
		if (clientsToRemove.length > 0) {
			await firstValueFrom(this.removeClientsFromContext(clientsToRemove));
		}

		if (clientsToAdd.length > 0) {
			await firstValueFrom(this.addClientsToContext(clientsToAdd));
		}
	}

	private convertToIntakeFormContextItems(
		context: ClientIntakeFormContext,
	): ClientIntakeFormContextItem[] {
		const items: ClientIntakeFormContextItem[] = [];

		if (context.clients) {
			items.push({
				type: 'clients',
				id: IntakeFormContextFsDocType.CLIENT_TAGS,
				data: context.clients,
			});
		}

		if (context.general) {
			items.push({
				type: 'general',
				id: IntakeFormContextFsDocType.ON_MATTER_UPDATE,
				data: context.general,
			});
		}

		return items;
	}
}
